import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";


const Tabs = ({
  tabs,
  tabContents,
  activeTab: externalActiveTab,
  onTabChange,
  isDeletable,
  onRemoveTab,
  tabVariants = [],
  keepAlive = [],
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const tabsContainerRef = useRef(null);
  const [tabDimensions, setTabDimensions] = useState({
    baseWidth: 200,
    minWidth: 80,
    maxWidth: 240,
    activeTabWidth: 260,
  });

  const activeTab =
    typeof externalActiveTab === "number" ? externalActiveTab : internalActiveTab;

  useEffect(() => {
    const calculateDimensions = () => {
      if (!tabsContainerRef.current) return;

      const containerWidth = tabsContainerRef.current.offsetWidth;
      const SCROLLBAR_WIDTH = 8;
      const isMobile = containerWidth < 640;
      const isTablet = containerWidth < 1024;

      const MIN_WIDTH = isMobile ? 80 : 60;
      const MAX_WIDTH = isMobile ? 120 : isTablet ? 160 : 200;
      const ACTIVE_WIDTH = isMobile ? 140 : isTablet ? 180 : 220;

      const availableWidth = containerWidth - SCROLLBAR_WIDTH;
      const remainingWidth = availableWidth - ACTIVE_WIDTH;
      const otherTabsWidth =
        tabs.length > 1 ? remainingWidth / (tabs.length - 1) : ACTIVE_WIDTH;

      setTabDimensions({
        baseWidth: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, otherTabsWidth)),
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
        activeTabWidth: ACTIVE_WIDTH,
      });
    };

    calculateDimensions();
    const resizeHandler = debounce(calculateDimensions, 100);
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, [tabs.length]);

  const handleTabChange = (index) => {
    if (onTabChange) onTabChange(index);
    else setInternalActiveTab(index);

    requestAnimationFrame(() => {
      setTimeout(() => {
        tabsContainerRef.current?.children[index]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }, 300);
    });
  };

  return (
    <div className="w-full h-full flex flex-col transition-none">
      {/* Tab Bar */}
      <div
        ref={tabsContainerRef}
        className="sticky top-0 z-10 flex overflow-x-scroll scrollbar-hide ml-[6px] pr-[7px] scroll-smooth"
        style={{
          scrollbarGutter: "stable",
          marginBottom: "-10px",
          paddingBottom: "3px",
        }}
      >
        {tabs.map((tab, index) => {
          const variant = tabVariants?.[index];
          const isDanger = variant === "danger";
          const isActive = activeTab === index;
          const baseContainerClass = isActive
            ? isDanger
              ? "border border-red-300 bg-red-50 shadow-[0_3px_8px_rgba(239,68,68,0.25)] translate-y-[-2px] z-10"
              : "border-l border-t border-r bg-white dark:bg-acs-dark-surface dark:border-acs-dark-border shadow-[0_3px_8px_rgba(0,0,0,0.2)] dark:shadow-[0_3px_8px_rgba(0,0,0,0.3)] translate-y-[-2px] z-10"
            : isDanger
            ? "border border-red-200 bg-red-100 text-red-700 hover:bg-red-50 shadow-[0_1px_3px_rgba(239,68,68,0.25)] hover:-translate-y-[1px] z-0"
            : "border border-gray-300 dark:border-acs-dark-border bg-[#F9FAFE] dark:bg-acs-dark-bg text-gray-600 dark:text-acs-dark-text hover:bg-gray-50 dark:hover:bg-acs-dark-hover shadow-[0_1px_3px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.2)] hover:-translate-y-[1px] z-0";

          const textClass = isActive
            ? isDanger
              ? "text-red-700"
              : "text-[#223688] dark:text-acs-dark-text"
            : isDanger
            ? "text-red-700 hover:text-red-800"
            : "text-gray-600 dark:text-acs-dark-subtext hover:text-[#223688] dark:hover:text-acs-dark-text";

          // if (!everMounted.has(index)) return null;

          return (
          <div
            key={`tab-${index}`}
            className={`relative transition-[flex-basis,transform,box-shadow] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                        will-change-[flex-basis,transform,box-shadow] transform-gpu ${baseContainerClass}`}
            style={{
              flex: `0 0 ${
                isActive
                  ? tabDimensions.activeTabWidth
                  : tabDimensions.baseWidth
              }px`,
              minWidth: `${tabDimensions.minWidth}px`,
              maxWidth: `${tabDimensions.maxWidth}px`,
            }}
          >
            {/* active highlight */}
            {isActive ? (
              <div
                className={`absolute bottom-0 left-0 right-0 h-[2px] ${
                  isDanger ? "bg-red-600" : "bg-[#223688]"
                }`}
              />
            ) : null}

            <div className="flex w-full items-center justify-center py-1.5 px-3 md:px-2 sm:px-1.5">
              <button
                type="button"
                className="min-w-0 flex-1 font-semibold flex justify-center items-center text-center truncate group"
                onClick={() => handleTabChange(index)}
              >
                <span
                  className={`truncate transition-opacity duration-200 text-xs md:text-sm font-roboto ${textClass}`}
                >
                  {tab}
                </span>
              </button>
              {isDeletable?.[index] ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveTab?.(index);
                  }}
                  className={`ml-2 shrink-0 transition-all duration-200 opacity-70 hover:opacity-100 hover:scale-110 origin-center cursor-pointer ${
                    activeTab === index
                      ? "text-gray-500 hover:text-gray-700"
                      : "text-gray-600 hover:text-gray-800 dark:text-acs-dark-subtext hover:dark:text-acs-dark-text"
                  }`}
                  aria-label="Close tab"
                >
                  <X size={11} strokeWidth={3} />
                </button>
              ) : null}
            </div>
          </div>
        );
        })}
      </div>

      {/* Animated Content Area */}
      <div className="flex-1 min-h-0 ml-2 mt-2 overflow-y-auto bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text p-0 sm:p-0 scrollbar-hide transition-none relative">
        {tabContents.map((content, index) => {
          const alive = keepAlive?.[index] ?? false;

          if (!alive) {
            // original behaviour — only render when active
            return index === activeTab ? (
              <div key={index} className="h-full min-h-0 flex flex-col">{content}</div>
            ) : null;
          }

          // keep-alive — mount once, hide with CSS
          return (
            <div
              key={index}
              className="h-full min-h-0 flex flex-col"
              style={{ display: index === activeTab ? "flex" : "none" }}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default Tabs;
